import { useCallback, useMemo, useRef, useState } from "react";
import * as workoutApi from "../../core/api/workoutApi";
import { useUtils } from "../../../../context/UtilsContext";
import { invalidateTemplateWorkoutDetailsPrefetch } from "../../core/workout-plan/workoutDetailsPrefetch";

export default function useWorkoutHandlers({
  get, post, context,
  templateId, planId, periodKey,
  updateWorkoutGroupTypeIds,
  setAlertMessage, setAlertType, workoutGroupTypeIds,
  clientId,
  workoutBundle = null,
  refreshBundle,
}) {
  const [savedWorkouts, setSavedWorkouts] = useState([]);
  const hasFetchedRef = useRef(false);
  const { sessionUtils, workoutCommonUtils } = useUtils();
  const eventTypes = useMemo(
    () => sessionUtils?.event_types?.[0]?.event_types || [],
    [sessionUtils?.event_types]
  );

  const workout_group_types = workoutCommonUtils?.workout_group_types || [];

  const applyPeriodFromBundle = useCallback(
    (bundle) => {
      if (!bundle) {
        setSavedWorkouts([]);
        return false;
      }
      const { workouts, workoutGroupTypeIds: groupIds } =
        workoutApi.periodWorkoutsFromBundle(bundle, workout_group_types, periodKey);
      setSavedWorkouts(workouts || []);
      if (groupIds) {
        updateWorkoutGroupTypeIds?.(periodKey, groupIds);
      }
      return (workouts || []).length > 0;
    },
    // updateWorkoutGroupTypeIds read via ref-quality: parent now memoizes it.
    [workout_group_types, periodKey, updateWorkoutGroupTypeIds]
  );

  const fetchSavedItems = useCallback(async () => {
    if (workoutBundle) {
      return applyPeriodFromBundle(workoutBundle);
    }

    const { workouts, workoutGroupTypeIds: groupIds } = await workoutApi.fetchSavedWorkouts(
      get,
      context,
      templateId,
      planId,
      periodKey,
      post,
      eventTypes,
      workout_group_types,
      clientId
    );

    setSavedWorkouts(workouts || []);
    if (groupIds) {
      updateWorkoutGroupTypeIds?.(periodKey, groupIds);
    }
    return (workouts || []).length > 0;
  }, [
    get,
    context,
    templateId,
    planId,
    periodKey,
    eventTypes,
    workout_group_types,
    post,
    updateWorkoutGroupTypeIds,
    clientId,
    workoutBundle,
    applyPeriodFromBundle,
  ]);

  const reloadAfterMutation = useCallback(async () => {
    if (context === "template" && templateId) {
      invalidateTemplateWorkoutDetailsPrefetch(templateId);
    }
    if (refreshBundle) {
      const bundle = await refreshBundle();
      if (bundle) {
        return applyPeriodFromBundle(bundle);
      }
    }
    return fetchSavedItems();
  }, [context, templateId, refreshBundle, applyPeriodFromBundle, fetchSavedItems]);

  const handleSaveWorkout = useCallback(async (activeWorkout, workoutDetails, sets) => {
    try {
      const res = await workoutApi.saveWorkout(
        post,
        get,
        context,
        activeWorkout,
        workoutDetails,
        sets,
        periodKey,
        eventTypes,
        workout_group_types,
        workoutGroupTypeIds,
        templateId,
        planId,
        clientId
      );
      if (res?.status === 200 || res?.data) {
        await reloadAfterMutation();
        setAlertMessage?.("Exercise added.");
        setAlertType?.("success");
        return true;
      }
      setAlertMessage?.("Error saving exercise.");
      setAlertType?.("error");
      return false;
    } catch (error) {
      setAlertMessage?.(error?.message || "Error saving exercise.");
      setAlertType?.("error");
      return false;
    }
  }, [
    post,
    get,
    context,
    periodKey,
    setAlertMessage,
    setAlertType,
    eventTypes,
    workout_group_types,
    workoutGroupTypeIds,
    templateId,
    planId,
    clientId,
    reloadAfterMutation,
  ]);

  const handleDeleteWorkout = useCallback(async (exerciseId) => {
    const res = await workoutApi.deleteWorkout(post, context, exerciseId, eventTypes);
    if (res.status === 200 || res.success) {
      await reloadAfterMutation();
      setAlertMessage?.("Exercise deleted.");
      setAlertType?.("success");
      return true;
    }
    setAlertMessage?.("Failed to delete exercise.");
    setAlertType?.("error");
    return false;
  }, [post, context, setAlertMessage, setAlertType, eventTypes, reloadAfterMutation]);

  const handleUpdateWorkout = useCallback(async (activeWorkout, payload) => {
    try {
      const res = await workoutApi.updateWorkout(
        post,
        context,
        activeWorkout.id,
        payload.sets,
        eventTypes,
        payload.workoutRow || null
      );

      if (res.success || res.status === 200) {
        await reloadAfterMutation();
        setAlertMessage?.("Exercise updated.");
        setAlertType?.("success");
        return true;
      }
      setAlertMessage?.("Failed to update exercise.");
      setAlertType?.("error");
      return false;
    } catch (error) {
      setAlertMessage?.(error?.message || "Failed to update exercise.");
      setAlertType?.("error");
      return false;
    }
  }, [post, context, setAlertMessage, setAlertType, eventTypes, reloadAfterMutation]);

  const handleExerciseSearch = useCallback(async (query, limit = 30) => {
    try {
      return (await workoutApi.excerciseList(get, query, limit)) || [];
    } catch (error) {
      console.error("Failed to search exercises:", error);
      setAlertMessage?.("Failed to search exercises.");
      setAlertType?.("error");
      return [];
    }
  }, [get, setAlertMessage, setAlertType]);

  const handleUpdateSequenceOrder = useCallback(async (workouts, ctx, groupTypeIds, key) => {
    try {
      const eventTypeId = eventTypes?.find(
        (x) => x.description === "modify_user_profile"
      )?.id;

      if (!eventTypeId) {
        setAlertMessage?.("Event type ID not found.");
        setAlertType?.("error");
        return false;
      }

      const targetGroupTypeID = groupTypeIds?.[key]?.id;
      if (!targetGroupTypeID) {
        setAlertMessage?.("Workout group not found.");
        setAlertType?.("error");
        return false;
      }

      const isTemplate = ctx === "template";
      const groupTypeField = isTemplate
        ? "template_workout_group_type_id"
        : "target_plan_workout_group_type_id";

      const workoutsPayload = workouts.map((w, index) => ({
        operation: "UPDATE",
        id: w.id,
        [groupTypeField]: targetGroupTypeID,
        workout_id: w.exercise_id || w.workout_id,
        sequence_order: index + 1,
        rest_time_after_seconds: w.rest_time_after || 0,
      }));

      const res = await workoutApi.updateWorkoutSequence(
        post,
        eventTypeId,
        workoutsPayload,
        ctx
      );

      if (res?.status === 200 || res?.success) {
        setAlertMessage?.("Order updated.");
        setAlertType?.("success");
        return true;
      }

      setAlertMessage?.("Failed to update order.");
      setAlertType?.("error");
      return false;
    } catch (error) {
      console.error("Failed to update sequence order:", error);
      setAlertMessage?.("Failed to update order.");
      setAlertType?.("error");
      return false;
    }
  }, [post, eventTypes, setAlertMessage, setAlertType]);

  return {
    savedWorkouts,
    setSavedWorkouts,
    hasFetchedRef,
    fetchSavedItems,
    applyPeriodFromBundle,
    reloadAfterMutation,
    handleSaveWorkout,
    handleDeleteWorkout,
    handleUpdateWorkout,
    handleExerciseSearch,
    handleUpdateSequenceOrder,
  };
}
